import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useFormContext } from 'react-hook-form'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { Image, Space } from '@tribeplatform/gql-client/types'
import {
  useCollections,
  useLocaleGroup,
  useSpace,
  useUpdateLocaleGroup,
  useUpdateSpace,
  useImageUploader,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import {
  FormControl,
  HelperText,
} from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useDomain } from '../../common/hooks/useDomain.js'
import { Form, FormMethods, FormRef } from '../../Form/index.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Link } from '../../Link/Link.js'
import { handleUploadFileError } from '../../utils/error.js'
import { SpaceImage } from '../SpaceImage.js'

import { ImagePicker } from './Components/ImagePicker.js'
import { SpaceSettingsProps } from './types.js'

export const FormDirtyUpdater = ({
  setDirty,
  isActive,
}: Pick<SpaceSettingsProps, 'setDirty' | 'isActive'>) => {
  const { formState } = useFormContext()

  useEffect(() => {
    if (isActive) {
      setDirty(formState.isDirty)
    }
  }, [formState.isDirty, isActive, setDirty])

  return null
}

export const SpaceSettingsGeneral = ({
  space,
  onClose,
  isChangingTab,
  confirmTabChange,
  rejectTabChange,
  setDirty,
  setSubmit,
  isActive,
}: SpaceSettingsProps) => {
  const { $t } = useI18n()
  const { getResourceUrl } = useDomain()
  const { data: collections } = useCollections()
  const { data } = useSpace({
    variables: {
      id: space.id,
    },
    fields: {
      authMemberProps: 'all',
      image: 'basic',
      collection: 'basic',
      banner: 'basic',
    },
  })
  const { mutateAsync: updateSpace, isLoading: isUpdatingSpace } =
    useUpdateSpace()
  const { mutateAsync: updateLocaleGroup } = useUpdateLocaleGroup()
  const buttonRef = useRef<HTMLButtonElement>()
  const { uploadImage, isLoading: isUploadingImages } = useImageUploader()
  const intl = useI18n()

  const { data: localeGroup } = useLocaleGroup({
    variables: { id: space.localeGroupId ?? '' },
    fields: { spaces: 'basic' },
    useQueryOptions: { enabled: Boolean(space.localeGroupId) },
  })

  if (data) {
    space = data
  }

  // Collection is a locale-group-wide setting managed on the primary space, so
  // only variant spaces are locked here (matching the web General Settings).
  // The primary space keeps an editable selector.
  const primarySpaceId = localeGroup?.primarySpaceId
  const isLocaleVariant =
    Boolean(space.localeGroupId) &&
    !!primarySpaceId &&
    space.id !== primarySpaceId
  const isLocalePrimary =
    Boolean(space.localeGroupId) &&
    !!primarySpaceId &&
    space.id === primarySpaceId
  const primarySpaceName = localeGroup?.spaces?.find(
    member => member.id === primarySpaceId,
  )?.name

  // While the group query is still resolving, primarySpaceId is undefined so both
  // isLocaleVariant and isLocalePrimary are false — the collection select would be
  // momentarily editable and a submit would route collectionId through updateSpace,
  // bypassing the group cascade. Treat this window as pending and lock it down.
  const localeGroupPending = Boolean(space.localeGroupId) && !localeGroup

  // Collection helper: variants link to the primary space's General settings
  // (not the localization page, which now redirects — so no dead end even when
  // the `locale-groups-enabled` flag is off); the primary notes it applies to
  // the whole group.
  let collectionHelperText: ReactNode
  if (isLocaleVariant) {
    collectionHelperText = (
      <HelperText status="info">
        <T
          id="LocaleGroup.Collection.Variant.Helper"
          defaultMessage="Managed by the primary space ({name})."
          values={{
            name:
              primarySpaceName ??
              $t({
                defaultMessage: 'primary',
                id: 'LocaleGroup.Primary.Short',
              }),
          }}
        />
        {primarySpaceId ? (
          <>
            {' '}
            <Link
              href={getResourceUrl(
                adminRoutes.site.spaces.general(primarySpaceId),
              )}
              target="_blank"
              trusted
              className="text-action-primary hover:underline"
            >
              <T
                id="LocaleGroup.Collection.OpenPrimary"
                defaultMessage="Open primary space settings"
              />
              <SvgIcon className="inline ms-1" name="link-external" />
            </Link>
          </>
        ) : null}
      </HelperText>
    )
  } else if (isLocalePrimary) {
    collectionHelperText = (
      <HelperText status="info">
        <T
          id="LocaleGroup.Collection.Primary.Helper"
          defaultMessage="Applies to all locales in this localization group."
        />
      </HelperText>
    )
  }

  useEffect(() => {
    if (isActive) {
      setSubmit(() => () => buttonRef?.current?.click())
    }
  }, [isActive, setSubmit, buttonRef])

  const [isSettingHomeViaHack, setHomePageHackFlag] = useState(false)

  const formRef = useRef<FormRef>()

  const getDefaultValues = useCallback(
    (space: Space) => ({
      ...space,
      _private: space?.private,
      collectionId: space?.collection?.id || null,
    }),
    [],
  )

  const defaultValues = useMemo(
    () => getDefaultValues(space),
    [getDefaultValues, space],
  )

  const isLoading = isUpdatingSpace

  useEffect(() => {
    if (formRef.current?.methods && !isLoading) {
      formRef.current.methods.reset(defaultValues)
    }
  }, [defaultValues, isLoading])

  const onUploadFile = useCallback(
    async (files: Array<File> | undefined) => {
      const file = files?.at(0)

      if (!file) {
        updateSpace({
          id: space.id,
          input: {
            bannerId: null,
          },
        })
        return
      }

      try {
        const uploadedImage = await uploadImage(file)
        if (uploadedImage?.id) {
          updateSpace({
            id: space.id,
            input: {
              bannerId: uploadedImage.id,
            },
          })
        }
      } catch (error) {
        handleUploadFileError(intl)(error)
      }
    },
    [updateSpace, space.id, uploadImage, intl],
  )

  const settingsLinkRenderer = useCallback(
    (chunks?: ReactNode) => {
      return (
        <Link
          href={getResourceUrl(
            `${adminRoutes.settings.network.root}#homepage-settings`,
          )}
          target="_blank"
          trusted
        >
          <strong>
            {chunks}
            <sup>
              <SvgIcon className="inline" name="link-external" />
            </sup>
          </strong>
        </Link>
      )
    },
    [getResourceUrl],
  )

  const collectionItems = useMemo(() => {
    return [
      {
        text: $t({
          defaultMessage: 'No collection',
          id: 'Admin.Space.Settings.Collection.None',
        }),
        value: null,
      },
      ...(collections ?? []).map(collection => ({
        text: collection.name,
        value: collection.id,
      })),
    ]
  }, [collections, $t])

  const onSubmit = async (
    data: ReturnType<typeof getDefaultValues>,
    methods: FormMethods<ReturnType<typeof getDefaultValues>>,
  ) => {
    const {
      name,
      description,
      address: { path },
      collectionId,
      _private,
      hidden,
      inviteOnly,
      nonAdminsCanInvite,
    } = data

    // Membership not yet resolved — don't risk routing collectionId through updateSpace.
    if (localeGroupPending) {
      return
    }

    if (path === '' && space.address.path !== '') {
      /**
       * The user is trying to make a new space its home using the legacy hack.
       * Action -> Warn the user and prevent the update.
       * The API must also enforce the validation, upon update.
       * In order to fully resolve the home space hack situation, the network initialization
       * workflow and the concept of system spaces should be revised.
       */
      toast({
        title: $t({
          defaultMessage:
            'Path cannot be empty. Use the administration settings to change the homepage.',
          id: 'Admin.Space.Settings.UpdateFailedForEmptyPath',
        }),
        status: 'error',
      })

      return
    }
    const isGroupMember = isLocaleVariant || isLocalePrimary
    const collectionChanged = collectionId !== (space.collection?.id ?? null)

    try {
      const updatedSpace = await updateSpace({
        id: space?.id,
        input: {
          name,
          description,
          address: { path, exact: true },
          // Collection is a locale-group-wide setting: members never write it
          // through updateSpace. The primary routes it through updateLocaleGroup
          // below so it cascades to every variant; variants can't edit it.
          collectionId: isGroupMember ? undefined : collectionId,
          private: _private,
          hidden,
          inviteOnly,
          nonAdminsCanInvite,
        },
      })

      if (isLocalePrimary && space.localeGroupId && collectionChanged) {
        await updateLocaleGroup({
          id: space.localeGroupId,
          input: { collectionId },
        })
      }

      methods.reset({ ...getDefaultValues(updatedSpace), collectionId })
      toast({
        title: $t({
          defaultMessage: '{SPACE_CC} settings updated',
          id: 'Admin.Space.Settings.Updated',
        }),
        status: 'success',
      })
      if (isChangingTab) {
        confirmTabChange()
      } else {
        onClose()
      }
    } catch (e) {
      const error = e?.response?.errors[0]
      if (error?.message) {
        toast({
          title: error.message,
          status: 'error',
        })
      } else {
        toast({
          title: $t({
            defaultMessage: 'Unable to update {SPACE} settings',
            id: 'Admin.Space.Settings.FailedToUpdate',
          }),
          status: 'error',
        })
      }
      if (isChangingTab) {
        rejectTabChange()
      }
    }
  }

  return (
    <Form ref={formRef} defaultValues={defaultValues} onSubmit={onSubmit}>
      {({ watch, setValue }) => {
        const [isPrivate, hidden] = watch(['_private', 'hidden'])
        if (!isPrivate && hidden) {
          setValue('hidden', false)
        }
        return (
          <>
            <FormDirtyUpdater isActive={isActive} setDirty={setDirty} />
            <div className="sm:h-132 flex flex-col">
              <div className="flex flex-col flex-grow overflow-auto p-5">
                <SectionHeader
                  title={$t({
                    defaultMessage: 'Settings',
                    id: 'Generics.Settings',
                  })}
                  className="pb-5"
                />
                <div className="space-y-5">
                  <FormControl.Layout
                    name="icon"
                    label={$t({
                      defaultMessage: 'Icon',
                      id: 'Generics.Icon',
                    })}
                  >
                    <div className="inline-block">
                      <SpaceImage space={space} size="2x" readOnly={false} />
                    </div>
                  </FormControl.Layout>
                  <Form.Input
                    label={$t({
                      defaultMessage: 'Name',
                      id: 'Generics.Name.Noun',
                    })}
                    name="name"
                    validation={{
                      required: $t({
                        defaultMessage: '{SPACE_CC} name cannot be empty.',
                        id: 'Admin.Space.Settings.Name.Validation',
                      }),
                    }}
                  />
                  <Form.Textarea
                    label={$t({
                      defaultMessage: 'Description',
                      id: 'Admin.Space.Settings.Description',
                    })}
                    name="description"
                  />

                  <div className="space-y-1">
                    <FormControl.Label>
                      <T
                        defaultMessage="Banner"
                        id="Admin.Space.Settings.Banner"
                      />
                    </FormControl.Label>
                    <ImagePicker
                      onUpload={onUploadFile}
                      isLoading={!!isUploadingImages}
                      thumbnailUrl={(space?.banner as Image)?.url}
                    />
                  </div>

                  <Form.Input
                    label={$t({
                      defaultMessage: 'Web address',
                      id: 'Admin.Space.Settings.WebAddress',
                    })}
                    name="address.path"
                    className="overflow-x-auto"
                    leadingAddon={getResourceUrl()}
                    onChange={input => {
                      if (space?.address.path !== '') {
                        if (input.target.value === '') {
                          setHomePageHackFlag(true)
                        } else {
                          setHomePageHackFlag(false)
                        }
                      }
                    }}
                    helperText={
                      isSettingHomeViaHack ? (
                        <HelperText status="error" highlighted>
                          <T
                            defaultMessage="Space address cannot be empty. If you are trying to change the homepage, please use the <settings-link>homepage settings</settings-link> in administration section."
                            id="SpaceSettings.EmptyAddressError"
                            values={{
                              'settings-link': settingsLinkRenderer,
                            }}
                          />
                        </HelperText>
                      ) : null
                    }
                  />
                  <Form.Select
                    label={$t({
                      defaultMessage: '{COLLECTION_CC}',
                      id: 'Generics.Collection',
                    })}
                    name="collectionId"
                    items={collectionItems}
                    // Only variants are locked; the primary keeps an editable
                    // selector (its edits cascade to the group via
                    // updateLocaleGroup on submit). Also locked while the group
                    // query is still resolving, since membership is unknown then.
                    disabled={isLocaleVariant || localeGroupPending}
                    helperText={collectionHelperText}
                  />
                  <Form.Toggle
                    helperText={
                      space.isHomepage && (
                        <HelperText status="warn">
                          <T
                            defaultMessage="This space is the homepage and cannot be made private. "
                            id="SpaceSettings.PrivateHomeWarning"
                          />
                        </HelperText>
                      )
                    }
                    disabled={space.isHomepage}
                    label={
                      <span
                        className={`flex ${
                          space.isHomepage
                            ? 'cursor-not-allowed text-content-disabled'
                            : ''
                        }`}
                      >
                        {$t({
                          defaultMessage: 'Make private',
                          id: 'Admin.Space.Settings.MakePrivate',
                        })}
                        {space.isHomepage && (
                          <SvgIcon className="ms-1" width="1rem" name="lock" />
                        )}
                      </span>
                    }
                    description={$t({
                      defaultMessage:
                        'Only members can see who’s in the space and what they post.',
                      id: 'Admin.Space.Settings.MakePrivate.Description',
                    })}
                    name="_private"
                  />
                  {isPrivate && (
                    <Form.Toggle
                      label={$t({
                        defaultMessage: 'Hide {SPACE}',
                        id: 'Admin.Space.Settings.Hide',
                      })}
                      description={$t({
                        defaultMessage:
                          '{SPACE_CC} will only be visible for its {MEMBERS}.',
                        id: 'Admin.Space.Settings.Hide.Description',
                      })}
                      name="hidden"
                    />
                  )}
                  <Form.Toggle
                    label={$t({
                      defaultMessage: 'Make invite-only',
                      id: 'Admin.Space.Settings.InviteOnly',
                    })}
                    description={$t({
                      defaultMessage:
                        'Allow only people with an invite to join as space members.',
                      id: 'Admin.Space.Settings.InviteOnly.Description',
                    })}
                    name="inviteOnly"
                  />
                  <Form.Toggle
                    label={$t({
                      defaultMessage: 'Anyone can invite',
                      id: 'Admin.Space.Settings.OpenInvite',
                    })}
                    description={$t({
                      defaultMessage:
                        'Allow non-admin members to invite others to the space.',
                      id: 'Admin.Space.Settings.OpenInvite.Description',
                    })}
                    name="nonAdminsCanInvite"
                  />
                </div>
              </div>
              <div className="flex justify-end px-5 py-5 border-t space-s-2">
                <Button
                  type="button"
                  variant="secondaryNeutral"
                  onClick={() => onClose()}
                >
                  <T defaultMessage="Cancel" id="Generics.Cancel" />
                </Button>
                <Button
                  type="submit"
                  loading={isLoading}
                  ref={buttonRef}
                  disabled={isSettingHomeViaHack || localeGroupPending}
                >
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </div>
            </div>
          </>
        )
      }}
    </Form>
  )
}
